import { formatDuration } from './durationFormat'
import { interventionForImputation, interventionForPresence } from './interventionIndex'
import { parseIsoInstant } from './timeGrid'

// TimelineView (03-dominio.md): turns the response of GET dataCorrection/timeline into blocks, lanes and segments.
// Pure: no React, no API. Every visible text is made with the `t_` it receives, so the same model serves the screen
// and the tests (which pass the real `es` dictionary).
//
//   buildTimelineView(response, { view, workerFilter, t_, range: { fromMs, toMs }, lookupDeviceName, lookupWorkerName })
//
// `workerFilter` is the worker chosen in the filters ('' = none). `lookup*Name(id)` give the catalog name of a machine
// or worker the response does not name ('' when unknown); the last resort is «#id» (RN77).

const TABLE_BY_LANE = {
  status: 'DEVICE_STATUS',
  job: 'DEVICE_FABRICATION_ORDER',
  presence: 'DEVICE_WORKER',
  imputations: 'DEVICE_WORKER_FABRICATION_ORDER'
}

const noName = () => ''

const workerFullName = (worker) => [worker.name, worker.surname].filter(Boolean).join(' ')

// Colour and label of a row by lane (RN25, legend of the maqueta). "Active" is decided by the TYPE of the state
// (typeCode), never by its statusCode (#189 RN26).
const JOB_TYPE_LOOK = {
  ACTIVE: { tone: 'blue', label: 'running' },
  INTERRUPTED: { tone: 'amber', label: 'paused' },
  FINISHED: { tone: 'green', label: 'finished' }
}

const describeRow = (lane, row, t_) => {
  if (lane === 'status') return row.status === 'WORKING' ? { tone: 'blue', base: t_('working') } : { tone: 'grey', base: t_('stopped') }
  if (lane === 'presence') return { tone: 'teal', base: t_('connected') }
  if (lane === 'imputations') return row.job ? { tone: 'blue', base: row.job.name || row.job.code } : { tone: 'grey', base: t_('stopped') }
  if (!row.state) {
    const jobName = row.job ? row.job.name || row.job.code : t_('Without job')
    return { tone: 'grey', base: row.fabricationOrder ? `${jobName} · ${row.fabricationOrder.code}` : jobName }
  }
  const look = JOB_TYPE_LOOK[row.state.typeCode]
  return look ? { tone: look.tone, base: t_(look.label) } : { tone: 'grey', base: row.state.statusName || row.state.statusCode }
}

// Correction marks (RN27, #189 RN40): `synthetic` = filled by a correction (nobody clocked it); `corrected` alone =
// adjusted. Both carry the ✎ prefix; neither links anywhere (there is no history yet, RN7).
const markOf = (correction) => {
  if (correction && correction.synthetic) return 'filled'
  if (correction && correction.corrected) return 'adjusted'
  return 'none'
}

const MARK_TEXT = { adjusted: 'Adjusted by a correction', filled: 'Filled by a correction (nobody clocked it)' }

// RN30 [D3, PO]: a clock-in WITH a job or a presence lasting more than 10 h (strict, so exactly 10 h is not) is
// suspicious. An open row lasts until the response's now. Only clock-ins show the duration badge.
export const SUSPICIOUS_THRESHOLD_MS = 10 * 3600 * 1000

const interventionIndexOf = (lane, row, interventions) => {
  if (lane === 'presence') return interventionForPresence(interventions, row.rowId)
  if (lane === 'imputations' && row.job && row.worker) return interventionForImputation(interventions, row.worker.id, row.rowId)
  return -1
}

/** The colour of a row by lane kind (`status`, `job`, `presence`, `imputations`): the mini tapes of the preview use the same palette. */
export const rowTone = (lane, row) => describeRow(lane, row, (key) => key).tone

// One row of a lane as a segment. `startMs`/`endMs` are what is PAINTED (clipped to the range; an open row reaches the
// response's now, RN24); `realStartMs`/`realEndMs` are what Sun recorded (an open row has no end: the response's now
// stands in for it, which is what its duration and the suspicious rule use, RN30). A row entirely outside the range
// gives no segment (the rows arrive unclipped, #189 RN21).
const buildSegment = (lane, row, { nowMs, range, t_, interventions }) => {
  const realStartMs = parseIsoInstant(row.start).getTime()
  const open = row.open === true
  const realEndMs = open ? nowMs : parseIsoInstant(row.end).getTime()
  const startMs = Math.max(realStartMs, range.fromMs)
  const endMs = Math.min(realEndMs, range.toMs)
  if (endMs <= startMs && !(realEndMs === realStartMs && realStartMs >= range.fromMs && realStartMs < range.toMs)) return undefined
  const { tone, base } = describeRow(lane, row, t_)
  const mark = markOf(row.correction)
  const durationMs = realEndMs - realStartMs
  const suspicious = (lane === 'presence' || (lane === 'imputations' && Boolean(row.job))) && durationMs > SUSPICIOUS_THRESHOLD_MS
  const text = mark === 'none' ? base : `✎ ${base}`
  return {
    key: `${TABLE_BY_LANE[lane]}:${row.rowId}`,
    table: TABLE_BY_LANE[lane],
    rowId: row.rowId,
    startMs,
    endMs,
    realStartMs,
    realEndMs,
    open,
    tone,
    mark,
    withJob: Boolean(row.job),
    workerId: row.worker ? row.worker.id : 0,
    // The intervention this segment opens (RN37): only presence rows and clock-ins with a job can, and only when the view
    // is built for someone who can correct (RN5). -1 = not interactive.
    interventionIndex: interventionIndexOf(lane, row, interventions),
    durationMs,
    suspicious,
    badge: suspicious && lane === 'imputations' ? formatDuration(durationMs, t_) : '',
    label: open ? `${text} · ${t_('in progress')}` : text,
    // The case (A1, AN, B1, BN) is Sun's, read by key (it is the last key of the row, #189 contrato §7): never computed here.
    tooltip: { what: lane === 'status' && row.status ? t_(row.status) : base, lane: '', caseText: row.case ? t_('Case {{case}}', { case: row.case }) : '', markText: mark === 'none' ? '' : t_(MARK_TEXT[mark]) }
  }
}

// Parallel clock-ins (the B cases) are split into sub-rows, group by group, so a lone clock-in keeps the full height
// (maqueta `stack`, RN29). Only clock-ins WITH a job take part; the others stay at index 0 of 1.
const assignSubRows = (segments) => {
  segments.forEach((segment) => { segment.subRow = 0; segment.subRows = 1 })
  const working = segments.filter((segment) => segment.withJob).sort((a, b) => a.startMs - b.startMs)
  let group = []
  let groupEnd = -Infinity
  let laneEnds = []
  const flush = () => {
    group.forEach((segment) => { segment.subRows = Math.max(1, laneEnds.length) })
    group = []
    laneEnds = []
  }
  working.forEach((segment) => {
    if (segment.startMs >= groupEnd) { flush(); groupEnd = -Infinity }
    let index = 0
    while (index < laneEnds.length && laneEnds[index] > segment.startMs) index++
    laneEnds[index] = segment.endMs
    segment.subRow = index
    group.push(segment)
    groupEnd = Math.max(groupEnd, segment.endMs)
  })
  flush()
  return segments
}

const segmentsOf = (lane, rows, context) => {
  const segments = rows.map((row) => buildSegment(lane, row, context)).filter(Boolean)
  return lane === 'imputations' ? assignSubRows(segments) : segments
}

const jobLanesOf = (block, { focusWorkerId, t_ }, context) => {
  const rowsByJob = new Map()
  block.lanes.jobStates.forEach((row) => {
    const jobId = row.job ? row.job.id : 0
    if (!rowsByJob.has(jobId)) rowsByJob.set(jobId, [])
    rowsByJob.get(jobId).push(row)
  })
  const touchedByFocus = (jobId) => block.lanes.imputations.some((row) => row.worker && row.worker.id === focusWorkerId && row.job && row.job.id === jobId) ||
    block.interventions.some((intervention) => intervention.worker.id === focusWorkerId && intervention.job && intervention.job.id === jobId)
  const lanes = []
  const withoutJob = []
  rowsByJob.forEach((rows, jobId) => {
    if (jobId === 0) { withoutJob.push(...rows); return }
    if (focusWorkerId !== '' && !touchedByFocus(jobId)) return
    const first = rows[0]
    const segments = segmentsOf('job', rows, context)
    const stateOf = new Map(rows.map((row) => [row.rowId, row.state]))
    lanes.push({
      key: `job-${jobId}`,
      kind: 'job',
      label: first.job.name || first.job.code,
      sub: t_('job · {{code}}', { code: first.fabricationOrder ? first.fabricationOrder.code : first.job.code }),
      jobId,
      workerId: '',
      notApplicable: '',
      // Finished during the whole period painted: every segment of the range is FINISHED (PO 2026-10-17, hidden by default).
      finished: segments.length > 0 && segments.every((segment) => (stateOf.get(segment.rowId) || {}).typeCode === 'FINISHED'),
      segments
    })
  })
  if (withoutJob.length && focusWorkerId === '') {
    lanes.push({ key: 'job-none', kind: 'job', label: t_('Without job'), sub: '', jobId: 0, workerId: '', notApplicable: '', segments: segmentsOf('job', withoutJob, context) })
  }
  return lanes
}

const deviceNameOf = (block, { lookupDeviceName }) => block.device.name || lookupDeviceName(block.device.id) || `#${block.device.id}`

const workerTitleOf = (block, { workerFilter, lookupWorkerName }) => {
  const worker = block.workers.find((candidate) => candidate.id === workerFilter)
  if (worker) return `${workerFullName(worker)} (${worker.code})`
  return lookupWorkerName(workerFilter) || `#${workerFilter}`
}

const MODE_CHIPS = { AUTO: { label: 'Automatic machine', tone: 'info' }, OFFLINE: { label: 'Manual machine', tone: 'default' } }

const buildBlock = (block, options, nowMs) => {
  const { t_, workerFilter } = options
  // Without DATA_CORRECTION_REQUEST nothing is interactive: no intervention is looked up at all (RN5).
  const context = { nowMs, range: options.range, t_, interventions: options.selectable ? block.interventions : [] }
  const focusWorkerId = workerFilter
  const workers = block.workers.filter((worker) => focusWorkerId === '' || worker.id === focusWorkerId)
  const automatic = block.device.mode === 'AUTO'
  const lanes = [{
    key: 'status',
    kind: 'status',
    label: t_('Machine'),
    sub: t_('state'),
    jobId: 0,
    workerId: '',
    notApplicable: automatic ? t_('Not applicable on automatic machines, where the machine itself sets this state.') : '',
    segments: automatic ? [] : segmentsOf('status', block.lanes.deviceStatus, context)
  }]
  lanes.push(...jobLanesOf(block, { focusWorkerId, t_ }, context))
  workers.forEach((worker) => {
    const label = workerFullName(worker)
    lanes.push({
      key: `presence-${worker.id}`,
      kind: 'presence',
      label,
      sub: t_('presence'),
      jobId: 0,
      workerId: worker.id,
      notApplicable: '',
      segments: segmentsOf('presence', block.lanes.presence.filter((row) => row.worker.id === worker.id), context)
    })
    lanes.push({
      key: `imputations-${worker.id}`,
      kind: 'imputations',
      label,
      sub: t_('clock-ins'),
      jobId: 0,
      workerId: worker.id,
      notApplicable: '',
      segments: segmentsOf('imputations', block.lanes.imputations.filter((row) => row.worker && row.worker.id === worker.id), context)
    })
  })
  const chips = []
  const modeChip = MODE_CHIPS[block.device.mode]
  if (modeChip) chips.push({ key: 'mode', label: t_(modeChip.label), tone: modeChip.tone })
  lanes.forEach((lane) => lane.segments.forEach((segment) => { segment.tooltip.lane = `${lane.label} · ${lane.sub}` }))
  const hasSuspicious = lanes.some((lane) => lane.segments.some((segment) => segment.suspicious))
  if (hasSuspicious) chips.push({ key: 'suspicious', label: t_('Suspicious clock-in'), tone: 'error' })
  const applied = appliedCorrectionsOf(block.corrections)
  if (applied > 0) chips.push({ key: 'corrections', label: t_(applied === 1 ? '{{count}} correction' : '{{count}} corrections', { count: applied }), tone: 'default' })
  const byMachine = options.view === 'device'
  return {
    key: String(block.device.id),
    device: block.device,
    raw: block,
    interventions: block.interventions,
    title: byMachine ? deviceNameOf(block, options) : workerTitleOf(block, options),
    subtitle: byMachine ? t_(workers.length === 1 ? '{{count}} worker' : '{{count}} workers', { count: workers.length }) : deviceNameOf(block, options),
    chips,
    hasSuspicious,
    lanes
  }
}

/**
 * The chip «N corrections» of a block (RN22 of #202): the APPLIED ones. Story 211 (RN18, D-S13): a «close now» and its trim
 * (`parentCorrectionId` = the id of a CLOSE_NOW of the same block) count as ONE correction.
 */
export const appliedCorrectionsOf = (corrections) => {
  const closes = new Set(corrections.filter((correction) => correction.kind === 'CLOSE_NOW').map((correction) => correction.id))
  const keys = corrections
    .filter((correction) => correction.status === 'APPLIED')
    .map((correction) => (correction.parentCorrectionId && closes.has(correction.parentCorrectionId) ? correction.parentCorrectionId : correction.id))
  return new Set(keys).size
}

export const buildTimelineView = (response, options) => {
  const settled = { lookupDeviceName: noName, lookupWorkerName: noName, ...options }
  const nowMs = parseIsoInstant(response.now).getTime()
  return {
    nowMs,
    labelHeader: settled.view === 'device' ? settled.t_('Machine · workers') : settled.t_('Worker · machine'),
    blocks: response.timelines.map((block) => buildBlock(block, settled, nowMs))
  }
}

/**
 * PO review of 2026-10-17 (S62): the jobs finished during the whole period painted are hidden unless «Show finished jobs» is on;
 * a job the selected intervention touches (`keepLaneKeys`, «blockKey|laneKey») is never hidden. The block says how many it hides.
 */
export const hideFinishedJobs = (view, { t_, keepLaneKeys = [] }) => ({
  ...view,
  blocks: view.blocks.map((block) => {
    const hidden = block.lanes.filter((lane) => lane.finished && !keepLaneKeys.includes(`${block.key}|${lane.key}`))
    if (hidden.length === 0) return block
    const label = t_(hidden.length === 1 ? '{{count}} finished job hidden' : '{{count}} finished jobs hidden', { count: hidden.length })
    return { ...block, lanes: block.lanes.filter((lane) => !hidden.includes(lane)), chips: [...block.chips, { key: 'finished', label, tone: 'default' }] }
  })
})

/** RN30: the blocks with some suspicious row in their visible lanes (the switch hides the others, it asks nothing). */
export const filterSuspicious = (view) => ({ ...view, blocks: view.blocks.filter((block) => block.hasSuspicious) })
